/**
 * Parses a Pi agent JSONL trace (see src/server/agents/pi.ts) into a
 * chronological timeline the UI can render in full — every thinking block,
 * every assistant text block, every tool call with its args/result, turn
 * boundaries, and anything we don't recognize (rendered as raw JSON rather
 * than dropped).
 *
 * Event shapes come from `@earendil-works/pi-coding-agent`'s
 * `MessageUpdateEvent` (`assistantMessageEvent` is a pi-ai
 * `AssistantMessageEvent`: text_start/text_delta/text_end,
 * thinking_start/thinking_delta/thinking_end, toolcall_start/delta/end) and
 * `TurnEndEvent` (`message`: full `AssistantMessage` with
 * `content: (TextContent | ThinkingContent | ToolCall)[]`, `toolResults`:
 * `ToolResultMessage[]`). `turn_end.message` is authoritative and complete,
 * so it's the primary source for a turn's blocks; delta accumulation from
 * `message_update` is the fallback for a turn that never closes (aborted
 * mid-stream), and drives the ordering of blocks-in-progress either way.
 */

export interface TraceEvent {
  ts?: string;
  type: string;
  [key: string]: unknown;
}

export interface ThinkingBlock {
  kind: 'thinking';
  text: string;
  complete: boolean;
}

export interface TextBlock {
  kind: 'text';
  text: string;
  complete: boolean;
}

export interface ToolResult {
  text: string;
  isError: boolean;
}

export interface ToolBlock {
  kind: 'tool';
  id: string;
  name: string;
  args: unknown;
  result?: ToolResult;
  startTs?: string;
  endTs?: string;
  durationMs?: number;
  complete: boolean;
}

export interface UnknownBlock {
  kind: 'unknown';
  contentType: string;
  raw: unknown;
}

export type TurnBlock = ThinkingBlock | TextBlock | ToolBlock | UnknownBlock;

export interface TurnUsage {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  reasoning?: number;
  totalTokens: number;
  costTotal: number;
}

export interface TurnItem {
  kind: 'turn';
  turnIndex: number;
  startTs?: string;
  endTs?: string;
  blocks: TurnBlock[];
  complete: boolean;
  usage?: TurnUsage;
}

export interface RunStartItem {
  kind: 'run_start';
  ts?: string;
  model?: string;
  label?: string;
  maxTurns?: number;
  tools?: string[];
  systemPrompt?: string;
  userPrompt?: string;
}

export interface RunEndItem {
  kind: 'run_end';
  ts?: string;
  turns?: number;
  toolCalls?: number;
  aborted?: boolean;
  tokens?: unknown;
  cost?: number;
  finalText?: string;
}

export interface RunErrorItem {
  kind: 'run_error';
  ts?: string;
  error?: string;
}

export interface RawItem {
  kind: 'raw';
  ts?: string;
  type: string;
  raw: unknown;
}

export type TimelineItem = RunStartItem | TurnItem | RunEndItem | RunErrorItem | RawItem;

/** Parses raw JSONL text into events. A line that fails to parse becomes an `unparsed` raw event rather than being dropped. */
export function parseTraceEvents(text: string): TraceEvent[] {
  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line): TraceEvent => {
      try {
        const obj = JSON.parse(line) as Record<string, unknown>;
        return { ...obj, type: typeof obj.type === 'string' ? obj.type : 'unparsed' } as TraceEvent;
      } catch {
        return { type: 'unparsed', raw: line };
      }
    });
}

function extractContentText(content: unknown): string {
  if (!Array.isArray(content)) return typeof content === 'string' ? content : JSON.stringify(content);
  return content
    .map((part) => {
      if (part && typeof part === 'object') {
        const p = part as Record<string, unknown>;
        if (p.type === 'text' && typeof p.text === 'string') return p.text;
        if (p.type === 'image') return '[image]';
      }
      return JSON.stringify(part);
    })
    .join('\n');
}

interface FallbackAcc {
  kind: 'thinking' | 'text' | 'tool';
  text?: string;
  toolCall?: { id: string; name: string; arguments: unknown };
}

/** Builds the in-progress-turn fallback used when a turn never gets a `turn_end` (aborted mid-stream). */
function fallbackBlocks(
  order: number[],
  acc: Map<number, FallbackAcc>,
  toolTimings: Map<string, { startTs?: string; endTs?: string }>,
  toolResults: Map<string, ToolResult>,
): TurnBlock[] {
  return order.map((idx): TurnBlock => {
    const entry = acc.get(idx);
    if (!entry) return { kind: 'unknown', contentType: 'missing', raw: { contentIndex: idx } };
    if (entry.kind === 'tool') {
      const tc = entry.toolCall;
      if (!tc) return { kind: 'unknown', contentType: 'toolcall (incomplete)', raw: { contentIndex: idx } };
      const timing = toolTimings.get(tc.id);
      return {
        kind: 'tool',
        id: tc.id,
        name: tc.name,
        args: tc.arguments,
        result: toolResults.get(tc.id),
        startTs: timing?.startTs,
        endTs: timing?.endTs,
        durationMs:
          timing?.startTs && timing?.endTs
            ? new Date(timing.endTs).getTime() - new Date(timing.startTs).getTime()
            : undefined,
        complete: toolResults.has(tc.id),
      };
    }
    return { kind: entry.kind, text: entry.text ?? '', complete: false };
  });
}

/** Turns a flat list of trace events into a chronological, fully-reconstructed timeline. */
export function buildTimeline(events: TraceEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [];

  let currentTurn: TurnItem | null = null;
  let blockOrder: number[] = [];
  let blockAcc: Map<number, FallbackAcc> = new Map();

  // toolCallId -> timing, populated from tool_execution_start/end (which carry
  // wall-clock `ts`, unlike the message content itself).
  const toolTimings = new Map<string, { startTs?: string; endTs?: string }>();
  // toolCallId -> result, populated from tool_execution_end as a fallback for
  // turns that never reach turn_end.
  const toolResultFallback = new Map<string, ToolResult>();

  function openTurnBlock(idx: number, kind: 'thinking' | 'text' | 'tool') {
    if (!blockAcc.has(idx)) {
      blockAcc.set(idx, { kind, text: '' });
      blockOrder.push(idx);
    }
  }

  function closeCurrentTurnFallback() {
    if (currentTurn && !currentTurn.complete) {
      currentTurn.blocks = fallbackBlocks(blockOrder, blockAcc, toolTimings, toolResultFallback);
    }
    blockOrder = [];
    blockAcc = new Map();
  }

  let turnCounter = 0;

  for (const e of events) {
    switch (e.type) {
      case 'run_start': {
        items.push({
          kind: 'run_start',
          ts: e.ts,
          model: typeof e.model === 'string' ? e.model : undefined,
          label: typeof e.label === 'string' ? e.label : undefined,
          maxTurns: typeof e.maxTurns === 'number' ? e.maxTurns : undefined,
          tools: Array.isArray(e.tools) ? (e.tools as string[]) : undefined,
          systemPrompt: typeof e.systemPrompt === 'string' ? e.systemPrompt : undefined,
          userPrompt: typeof e.userPrompt === 'string' ? e.userPrompt : undefined,
        });
        break;
      }

      case 'turn_start': {
        closeCurrentTurnFallback();
        currentTurn = {
          kind: 'turn',
          turnIndex: typeof e.turnIndex === 'number' ? e.turnIndex : turnCounter,
          startTs: e.ts,
          blocks: [],
          complete: false,
        };
        turnCounter += 1;
        items.push(currentTurn);
        break;
      }

      case 'message_update': {
        const ame = e.assistantMessageEvent as Record<string, unknown> | undefined;
        if (!ame || typeof ame.type !== 'string') {
          items.push({ kind: 'raw', ts: e.ts, type: e.type, raw: e });
          break;
        }
        const idx = typeof ame.contentIndex === 'number' ? ame.contentIndex : -1;
        switch (ame.type) {
          case 'text_start':
            openTurnBlock(idx, 'text');
            break;
          case 'text_delta': {
            openTurnBlock(idx, 'text');
            const b = blockAcc.get(idx)!;
            b.text = (b.text ?? '') + (typeof ame.delta === 'string' ? ame.delta : '');
            break;
          }
          case 'text_end': {
            openTurnBlock(idx, 'text');
            const b = blockAcc.get(idx)!;
            b.text = typeof ame.content === 'string' ? ame.content : b.text;
            break;
          }
          case 'thinking_start':
            openTurnBlock(idx, 'thinking');
            break;
          case 'thinking_delta': {
            openTurnBlock(idx, 'thinking');
            const b = blockAcc.get(idx)!;
            b.text = (b.text ?? '') + (typeof ame.delta === 'string' ? ame.delta : '');
            break;
          }
          case 'thinking_end': {
            openTurnBlock(idx, 'thinking');
            const b = blockAcc.get(idx)!;
            b.text = typeof ame.content === 'string' ? ame.content : b.text;
            break;
          }
          case 'toolcall_start':
            openTurnBlock(idx, 'tool');
            break;
          case 'toolcall_delta':
            openTurnBlock(idx, 'tool');
            break;
          case 'toolcall_end': {
            openTurnBlock(idx, 'tool');
            const b = blockAcc.get(idx)!;
            const tc = ame.toolCall as { id: string; name: string; arguments: unknown } | undefined;
            if (tc) b.toolCall = tc;
            break;
          }
          default:
            // "start", "done", "error" and anything future: not dropped, just
            // not part of the block reconstruction — surface as raw context.
            items.push({ kind: 'raw', ts: e.ts, type: `message_update:${String(ame.type)}`, raw: e });
        }
        break;
      }

      case 'tool_execution_start': {
        const id = typeof e.toolCallId === 'string' ? e.toolCallId : undefined;
        if (id) toolTimings.set(id, { ...toolTimings.get(id), startTs: e.ts });
        break;
      }

      case 'tool_execution_end': {
        const id = typeof e.toolCallId === 'string' ? e.toolCallId : undefined;
        if (id) {
          toolTimings.set(id, { ...toolTimings.get(id), endTs: e.ts });
          toolResultFallback.set(id, {
            text: extractContentText((e.result as Record<string, unknown> | undefined)?.content ?? e.result),
            isError: Boolean(e.isError),
          });
        }
        break;
      }

      case 'turn_end': {
        blockOrder = [];
        blockAcc = new Map();
        if (currentTurn) {
          currentTurn.endTs = e.ts;
          currentTurn.complete = true;
          const msg = e.message as Record<string, unknown> | undefined;
          const toolResultsArr = Array.isArray(e.toolResults) ? (e.toolResults as Record<string, unknown>[]) : [];
          const resultsByCallId = new Map(
            toolResultsArr.map((r) => [
              r.toolCallId as string,
              { text: extractContentText(r.content), isError: Boolean(r.isError) } satisfies ToolResult,
            ]),
          );
          const content = Array.isArray(msg?.content) ? (msg!.content as Record<string, unknown>[]) : [];
          const blocks: TurnBlock[] = content.map((c): TurnBlock => {
            if (c.type === 'text') return { kind: 'text', text: String(c.text ?? ''), complete: true };
            if (c.type === 'thinking') return { kind: 'thinking', text: String(c.thinking ?? ''), complete: true };
            if (c.type === 'toolCall') {
              const id = String(c.id ?? '');
              const timing = toolTimings.get(id);
              return {
                kind: 'tool',
                id,
                name: String(c.name ?? ''),
                args: c.arguments,
                result: resultsByCallId.get(id) ?? toolResultFallback.get(id),
                startTs: timing?.startTs,
                endTs: timing?.endTs,
                durationMs:
                  timing?.startTs && timing?.endTs
                    ? new Date(timing.endTs).getTime() - new Date(timing.startTs).getTime()
                    : undefined,
                complete: true,
              };
            }
            return { kind: 'unknown', contentType: String(c.type ?? 'content'), raw: c };
          });
          currentTurn.blocks = blocks;
          const usage = msg?.usage as Record<string, unknown> | undefined;
          if (usage) {
            const cost = usage.cost as Record<string, unknown> | undefined;
            currentTurn.usage = {
              input: Number(usage.input ?? 0),
              output: Number(usage.output ?? 0),
              cacheRead: Number(usage.cacheRead ?? 0),
              cacheWrite: Number(usage.cacheWrite ?? 0),
              reasoning: typeof usage.reasoning === 'number' ? usage.reasoning : undefined,
              totalTokens: Number(usage.totalTokens ?? 0),
              costTotal: Number(cost?.total ?? 0),
            };
          }
        } else {
          items.push({ kind: 'raw', ts: e.ts, type: e.type, raw: e });
        }
        currentTurn = null;
        break;
      }

      case 'run_end': {
        items.push({
          kind: 'run_end',
          ts: e.ts,
          turns: typeof e.turns === 'number' ? e.turns : undefined,
          toolCalls: typeof e.toolCalls === 'number' ? e.toolCalls : undefined,
          aborted: typeof e.aborted === 'boolean' ? e.aborted : undefined,
          tokens: e.tokens,
          cost: typeof e.cost === 'number' ? e.cost : undefined,
          finalText: typeof e.finalText === 'string' ? e.finalText : undefined,
        });
        break;
      }

      case 'run_error': {
        items.push({ kind: 'run_error', ts: e.ts, error: typeof e.error === 'string' ? e.error : undefined });
        break;
      }

      default:
        items.push({ kind: 'raw', ts: e.ts, type: e.type, raw: e });
    }
  }

  // Trailing turn never closed (aborted mid-stream) — reconstruct from deltas.
  closeCurrentTurnFallback();

  return items;
}

// ---------------------------------------------------------------------------
// Trace fetch cache — dedupes concurrent/repeat fetches so re-expanding an
// already-loaded row (Activity accordion) or revisiting a detail page
// doesn't refetch the JSONL.
// ---------------------------------------------------------------------------

const traceCache = new Map<number, Promise<string>>();

export function fetchTraceText(runId: number, fetcher: (path: string) => Promise<string>): Promise<string> {
  const cached = traceCache.get(runId);
  if (cached) return cached;
  const promise = fetcher(`/runs/${runId}/trace`).catch((err) => {
    traceCache.delete(runId); // don't cache failures — allow retry
    throw err;
  });
  traceCache.set(runId, promise);
  return promise;
}
